import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  BookOpen, Briefcase, FileText, Receipt, CreditCard, PenTool,
  MessageSquare, Calendar, Shield, History, User, ChevronDown
} from 'lucide-react'
import { useI18n } from '../hooks/useI18n'
import '../styles/Help.css'

const SECTIONS = [
  {
    id: 'overview',
    icon: BookOpen,
    title: 'Welcome to Your Client Portal',
    items: [
      {
        q: 'What can I do here?',
        a: 'Your client portal is a secure space where you can access all information about your legal matters, view documents, pay invoices, message your attorney, and more — all in one place.'
      },
      {
        q: 'Is my data secure?',
        a: 'Yes. All data is encrypted in transit and at rest. Only you and your law firm’s authorized staff can access your information. Your firm follows strict confidentiality and data protection rules.'
      },
      {
        q: 'How do I change my language?',
        a: 'Use the language selector at the bottom of the sidebar. We currently support English and Filipino.'
      }
    ]
  },
  {
    id: 'matters',
    icon: Briefcase,
    title: 'My Matters',
    items: [
      {
        q: 'What is a matter?',
        a: 'A "matter" is the legal case or project your firm is handling for you. Each matter has its own documents, invoices, appointments, and messages.'
      },
      {
        q: 'How do I view matter details?',
        a: 'Click on any matter from the My Matters page to see the full details, including the assigned attorney, status, important dates, and related documents.'
      }
    ]
  },
  {
    id: 'documents',
    icon: FileText,
    title: 'Documents',
    items: [
      {
        q: 'How do I download a document?',
        a: 'Go to Documents, find the document you want, and click the Download button. Documents are typically in PDF or Word format.'
      },
      {
        q: 'How do I upload a document for my attorney?',
        a: 'Use the Upload button in Documents. Supported formats include PDF, Word, Excel, and common image types. Maximum file size is 25MB.'
      },
      {
        q: 'How do I sign a document?',
        a: 'When your attorney requests your signature, you’ll receive an email notification. Click the link in the email or check the Pending Signatures section in your portal. Review the document, then sign electronically with one click.'
      }
    ]
  },
  {
    id: 'invoices',
    icon: Receipt,
    title: 'Invoices & Payments',
    items: [
      {
        q: 'How do I view my invoices?',
        a: 'Click Invoices in the sidebar. You’ll see all your invoices with their status (paid, pending, overdue). Click any invoice to see the details and line items.'
      },
      {
        q: 'How do I pay an invoice?',
        a: 'On the invoice page, click "Pay Now". You can pay with credit/debit card, GCash, or GrabPay through our secure payment processor (PayMongo).'
      },
      {
        q: 'Where can I see my payment history?',
        a: 'Click "Payment History" in the sidebar to see all past payments with dates, amounts, and reference numbers.'
      }
    ]
  },
  {
    id: 'trust',
    icon: Shield,
    title: 'Trust Account',
    items: [
      {
        q: 'What is a trust account?',
        a: 'A trust account (also called IOLTA) is a special account where your attorney holds funds on your behalf — for example, retainers, settlement proceeds, or money set aside to pay third parties (court fees, experts).'
      },
      {
        q: 'How do I check my trust balance?',
        a: 'Click "Trust Account" in the sidebar to see your current balance and any matter-specific trust accounts.'
      }
    ]
  },
  {
    id: 'messages',
    icon: MessageSquare,
    title: 'Messages',
    items: [
      {
        q: 'How do I message my attorney?',
        a: 'Click Messages in the sidebar, then "New Message". Select the matter you want to discuss, enter a subject and your message, and click Send.'
      },
      {
        q: 'How will I know if I get a reply?',
        a: 'You’ll see a notification badge on the Messages icon when there are unread messages. You may also receive an email notification depending on your firm’s settings.'
      },
      {
        q: 'Are messages private?',
        a: 'Yes. Messages are encrypted and only visible to you and your attorney. They are part of your attorney-client privileged communication.'
      }
    ]
  },
  {
    id: 'appointments',
    icon: Calendar,
    title: 'Appointments',
    items: [
      {
        q: 'How do I see my appointments?',
        a: 'Click Appointments in the sidebar to see all scheduled meetings with your attorney, including the date, time, location, and any notes.'
      },
      {
        q: 'Can I request an appointment?',
        a: 'Yes — message your attorney through the Messages feature with your preferred dates and times. They will confirm and add it to your calendar.'
      }
    ]
  },
  {
    id: 'account',
    icon: User,
    title: 'Account & Profile',
    items: [
      {
        q: 'How do I update my contact information?',
        a: 'Go to Profile in the sidebar. You can update your name, phone number, address, and other contact details.'
      },
      {
        q: 'How do I change my password?',
        a: 'On the Profile page, scroll to "Change Password". Enter your current password and your new password to update.'
      },
      {
        q: 'I forgot my password. What do I do?',
        a: 'On the login page, click "Forgot Password". Enter your email and we’ll send you a reset link.'
      }
    ]
  }
]

function Help() {
  const { t } = useI18n()
  const [openItem, setOpenItem] = useState(null)

  const toggleItem = (sectionId, itemIdx) => {
    const key = `${sectionId}-${itemIdx}`
    setOpenItem(openItem === key ? null : key)
  }

  return (
    <div className="help-page">
      <div className="page-header">
        <h1>Help & Guide</h1>
        <p>Everything you need to know about using your client portal</p>
      </div>

      <div className="help-sections">
        {SECTIONS.map((section, sIdx) => {
          const Icon = section.icon
          return (
            <motion.div
              key={section.id}
              className="help-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: sIdx * 0.05 }}
            >
              <div className="help-section-header">
                <div className="help-section-icon">
                  <Icon size={20} />
                </div>
                <h2 className="help-section-title">{section.title}</h2>
              </div>
              <div className="help-section-items">
                {section.items.map((item, idx) => {
                  const key = `${section.id}-${idx}`
                  const isOpen = openItem === key
                  return (
                    <div key={idx} className={`help-item ${isOpen ? 'open' : ''}`}>
                      <button
                        className="help-item-question"
                        onClick={() => toggleItem(section.id, idx)}
                      >
                        <span>{item.q}</span>
                        <ChevronDown size={16} className="help-item-chevron" />
                      </button>
                      {isOpen && (
                        <motion.div
                          className="help-item-answer"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                        >
                          <p>{item.a}</p>
                        </motion.div>
                      )}
                    </div>
                  )
                })}
              </div>
            </motion.div>
          )
        })}
      </div>

      <div className="help-footer">
        <p>Still have questions? Send a message to your attorney through the Messages feature, or contact your law firm directly.</p>
      </div>
    </div>
  )
}

export default Help
